import React, { useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable} from 'material-react-table';
import { Box, Button, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
// import * as XLSX from 'xlsx';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from "file-saver";
import moment from 'moment';
import { toAbsoluteUrl } from '../../../_metronic/helpers';
import { useAppSelector } from '../../../app/hooks';
import { parseISO, format, isValid, parse } from "date-fns"
import { determineCultureDateTimeFormat, determineCultureFormat, determineCultureFormatForTimesheet, formatCurrntDateTime, FormatInvoiceDecimalNumber } from '../../../common/timeLiveFunction';
import { formatAuditValue } from './AuditTrailFunctions';
import ExportToPDFModal from './ExportToPDFModal';

const MUISimpleTableView = (props: any) => {
    const { reportName, selectedFilters, showCompanyLogo, companyLogo } = props
    const { config = {}, data = [] } = props.table || {};
    const { decimalPlaces, isCompanyOwnLogo, cultureInfoName ,showFilterInformationOnExport} = useAppSelector(state => state.globalState);
    const [isOpen, setIsOpen] = useState(false);
    const [exportLoading, setExportLoading] = useState({
        pdf: false,
        csv: false,
        xls: false
    });

    const formatTimeWithoutDate = (dateString: any) => {
        if (!dateString) return '';
        const timeData = moment.parseZone(dateString).format('H:mm');
        return timeData;
    };

    const columns = useMemo(() => {
        const isBaseCurrencyColumn = (col: any) =>
            col.accessorKey?.startsWith('BaseCurrency') ||
            col.accessorKey === 'BaseCurrencyAmount' ||
            col.accessorKey === 'BaseCurrencyEstimatedCost' ||
            col.accessorKey === 'BaseCurrencyEmployeeCost' ||
            col.accessorLower === 'basecurrencytaxamount' ||
            col.accessorLower === 'basecurrencyreimbursementamount' ||
            col.accessorLower === 'basecurrencynetamount' ||
            col.accessorLower === 'basecurrencyemployeecost' ||
            col.accessorLower === 'basecurrencyprofit' ||
            col.accessorLower === 'basecurrencyamount';
        const isCurrencyColumn = (col: any) =>
            (col.accessorKey === 'BaseCurrencyAmount' || col.accessorKey === 'BaseCurrencyEstimatedCost' || col.accessorKey === 'BaseCurrencyEmployeeCost') ||
            col.type?.toLowerCase() === 'currencydecimal';

        const getCurrencyCode = (row: any, col: any) => {
        if (!isCurrencyColumn(col)) return '';        
        if (isBaseCurrencyColumn(col)) {
            return row.original.ParameterCurrencyCode || 'USD';
        }        
        return row.original.CurrencyCode || 'USD';
        };

        const formatCurrencyValue = (value: number, currencyCode: string) => {
            // Determine locale
            let locale = cultureInfoName.toString();
            if (locale === '' || locale === 'auto') {
                locale = 'en-US';
            }

            if (!currencyCode || typeof value !== 'number') {
                if (typeof value === 'number')
                    return FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, decimalPlaces);
            }
            try {
                return value?.toLocaleString(locale + "-u-nu-latn", {
                    style: 'currency',
                    currency: currencyCode === 'US$' ? 'USD' : currencyCode,
                    minimumFractionDigits: decimalPlaces,
                    maximumFractionDigits: decimalPlaces,
                });
            } catch {
                return value.toFixed(2);
            }
        };

        const baseColumns = (config.columns || []).map((col: any, index: number) => {

            const columnDef: any = {
                ...col,
            };

            if (col.accessorKey === 'OldValue' || col.accessorKey === 'NewValue') {
                columnDef.Cell = ({ cell, row }: any) => {
                    const value = cell.getValue();

                    const fieldName = row.original.FieldName || '';
                    return formatAuditValue(value, fieldName, cultureInfoName.toString(), decimalPlaces);
                };
            }

            if (index === 0 && config.summaryConfig.length > 0) {
                const hasAggregation = col.aggregationFn || (col.footer && typeof col.footer === 'string');

                if (hasAggregation) {
                    columnDef.Footer = ({ table }: any) => {
                        const rows = table.getFilteredRowModel().rows;
                        const footerType = col.footer?.toLowerCase() || '';
                        const value = footerType === 'sum'
                            ? rows.reduce((sum: any, row: any) => sum + (row.original[col.accessorKey] || 0), 0)
                            : footerType === 'count'
                                ? rows.length
                                : null;

                        return (
                            <Box fontWeight="bold" textAlign="left">
                                {value !== null ? value : 'Summary'}
                            </Box>
                        );
                    };
                } else {
                    columnDef.Footer = () => (
                        <div style={{
                            textAlign: "left",
                            fontWeight: "bold",
                            fontSize: "12px"
                        }}>
                            Full Summary
                        </div>
                    );
                }
            }

            if (col.footer && index !== 0) {
                const footerType = col.footer.toLowerCase();
                columnDef.aggregationFn = footerType === 'sum' ? 'sum' :
                    footerType === 'count' ? 'count' : undefined;

                columnDef.Footer = ({ table }: any) => {
                    const rows = table.getFilteredRowModel().rows;
                    const value = footerType === 'sum'
                        ? rows.reduce((sum: any, row: any) => sum + (row.original[col.accessorKey] || 0), 0)
                        : footerType === 'count'
                            ? rows.length
                            : null;
                    if (col.type?.toLowerCase() === 'currencydecimal') {
                        const currencyCode = rows[0] ? getCurrencyCode(rows[0], col) : 'USD';
                        return <Box textAlign="right">{formatCurrencyValue(value, currencyCode)}</Box>;
                    }
                    const decimalPointForHours = col?.accessorKey.toLowerCase() === 'totalhours' || col?.accessorKey.toLowerCase() === 'earned' || col?.accessorKey.toLowerCase() === 'consume' || col?.accessorKey.toLowerCase() === 'available' || col?.accessorKey.toLowerCase() === 'carryforward' || col?.accessorKey.toLowerCase() === 'initialsettohours' || col?.accessorKey.toLowerCase() === 'policyearnhours' || col?.accessorKey.toLowerCase() === 'resettohours' || col?.accessorKey.toLowerCase().includes('hours') || col?.accessorKey.toLowerCase().includes('hour');
                    return <Box textAlign="right">{decimalPointForHours ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, 2) : FormatInvoiceDecimalNumber(cultureInfoName.toString(), value, decimalPlaces)}</Box>; //Number(value).toFixed(decimalPlaces)
                };

                columnDef.AggregatedCell = ({ cell, row }: any) => {
                    if (col.type?.toLowerCase() === 'currencydecimal') {
                        return <strong>{formatCurrencyValue(cell.getValue(), getCurrencyCode(row, col))}</strong>;
                    }
                    return <strong>{cell.getValue()}</strong>;
                };
            }

            if (col.accessorKey.toLowerCase() === "date" && col.type === "text") {
                columnDef.Cell = ({ cell }: any) => {
                    const value = cell.getValue();
                    if (!value) return "";

                    const inputFormat = "dd MMM yyyy";

                    try {
                        if (value.includes(" - ")) {
                            const [startStr, endStr] = value.split(" - ");
                            const parsedStart = parse(startStr.trim(), inputFormat, new Date());
                            const parsedEnd = parse(endStr.trim(), inputFormat, new Date());
                            if (isValid(parsedStart) && isValid(parsedEnd)) {
                                const cultureFormat = determineCultureFormat(cultureInfoName.toString());
                                return `${format(parsedStart, cultureFormat)} - ${format(parsedEnd, cultureFormat)}`;
                            }
                        }

                        const parsedDate = parse(value, inputFormat, new Date());

                        if (!isValid(parsedDate)) return value;

                        return format(parsedDate, determineCultureFormat(cultureInfoName.toString()));

                    } catch (err) {
                        console.error("Date parse error:", value, err);
                        return value;
                    }
                };
            }

            if (col.accessorKey === "CreatedOn" || col.accessorKey === "ApprovedOn") {
                columnDef.Cell = ({ cell }: any) => {
                    const value = cell.getValue();
                    if (!value) return "";

                    try {
                        const parsedDate = parseISO(value);
                        if (!isValid(parsedDate)) return "";
                        return format(parsedDate, determineCultureDateTimeFormat(cultureInfoName.toString()));
                    } catch (err) {
                        console.error("Error parsing date:", value, err);
                        return "";
                    }
                };
            }

            if (col.accessorKey.toLowerCase() === 'lastlogintime') {
                columnDef.Cell = ({ cell }: any) => {
                    // const value = cell.getValue();
                    // return cell.getValue() && format(parseISO(cell.getValue()), determineCultureFormat(cultureInfoName.toString()))
                    const value = cell.getValue();
                    if (!value) return "";

                    let parsedDate;
                    if (typeof value === "string" && value.includes("-")) {
                        parsedDate = parseISO(value);
                    } else {
                        parsedDate = parse(value, "dd.MM.yyyy", new Date());
                    }

                    if (!isValid(parsedDate)) {
                        return value;
                    }

                    return formatCurrntDateTime(value, cultureInfoName?.toString());
                };
            }

            if (col.accessorKey.toLowerCase() === 'estimatedduration') {
                columnDef.Cell = ({ cell }: any) => {
                    const value = cell.getValue();
                    if (value == null || value == '') return;
                    return FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces);
                };
            }

            if (col.type && col.accessorKey !== 'OldValue' && col.accessorKey !== 'NewValue') {
                switch (col.type.toLowerCase()) {
                    case 'date':
                        columnDef.Cell = ({ cell }: any) => {
                            const value = cell.getValue();
                            if (!value) return "";

                            let parsedDate;
                            if (typeof value === "string" && value.includes("-")) {
                                parsedDate = parseISO(value);
                            } else {
                                parsedDate = parse(value, "dd.MM.yyyy", new Date());
                            }

                            if (!isValid(parsedDate)) {
                                console.warn("Invalid date:", value);
                                return value;
                            }
                            return format(
                                parsedDate,
                                determineCultureFormat(cultureInfoName.toString())
                            );
                        };
                        break;
                    case 'datetime':
                        columnDef.Cell = ({ cell }: any) => {
                            const value = cell.getValue();
                            if (!value) return "";

                            let parsedDate;
                            if (typeof value === "string" && value.includes("-")) {
                                parsedDate = parseISO(value);
                            } else {
                                parsedDate = parse(value, "dd.MM.yyyy", new Date());
                            }

                            if (!isValid(parsedDate)) {
                                return value;
                            }

                            return formatCurrntDateTime(value, cultureInfoName?.toString());
                        };
                        break;

                    case 'time':
                    case 'duration':
                        columnDef.Cell = ({ cell }: any) => {
                            if (cell.getValue() == null || cell.getValue() == '') return;
                            return cell.getValue().includes('T') ? formatTimeWithoutDate(cell.getValue()) : cell.getValue()
                        };
                        break;

                    case 'currencydecimal':
                        columnDef.Cell = ({ cell, row }: any) => {
                            const value = cell.getValue();
                            return (
                                <Box textAlign="right">
                                    {formatCurrencyValue(value, getCurrencyCode(row, col))}
                                </Box>
                            );
                        };
                        break;

                    case 'decimal':
                        columnDef.Cell = ({ cell }: any) => {
                            const decimalPointForHours = col?.accessorKey.toLowerCase() === 'totalhours' || col?.accessorKey.toLowerCase() === 'dayoff' || col?.accessorKey.toLowerCase() === 'earned' || col?.accessorKey.toLowerCase() === 'consume' || col?.accessorKey.toLowerCase() === 'available' || col?.accessorKey.toLowerCase() === 'carryforward' || col?.accessorKey.toLowerCase() === 'initialsettohours' || col?.accessorKey.toLowerCase() === 'policyearnhours' || col?.accessorKey.toLowerCase() === 'resettohours' || col?.accessorKey.toLowerCase() === 'estimatedhours' || col?.accessorKey.toLowerCase().includes('hours') || col?.accessorKey.toLowerCase().includes('hour');
                            const val = FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPointForHours ? 2 : decimalPlaces)

                            return (
                                <Box textAlign="right">{(val != null && typeof cell.getValue() === 'number') ? val : cell.getValue()}</Box>
                            )
                        };
                        break;
                    case 'number':
                        columnDef.Cell = ({ cell }: any) => {
                            const val = cell.getValue();
                            const headerText = col.header?.toString() ?? '';
                            if (headerText.toLowerCase().endsWith('id')) {
                                return <Box textAlign={'right'}>{val}</Box>;
                            }
                            if(col.accessorKey?.toLowerCase() === 'profilezonecount' ||  col.accessorKey?.toLowerCase() === 'gpsaccuracy' || col.accessorKey?.toLowerCase() === 'breakcount' || col.accessorKey?.toLowerCase() === 'violationcount'){
                                return <Box textAlign={'right'}>{val}</Box>;
                            }
                            if( col.accessorKey?.toLowerCase() === 'distancefromzonecenter' || col.accessorKey?.toLowerCase() === 'distancefromzoneboundary') {
                                return <Box textAlign={'right'}>{val != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), 0) : null}</Box>;
                            }
                            return (
                                <>
                                    {/* <Box textAlign="right">{val != null ? Number(val) : null}</Box> */}
                                    <Box textAlign={'right'}>{val != null ? FormatInvoiceDecimalNumber(cultureInfoName.toString(), cell.getValue(), decimalPlaces) : null}</Box>
                                </>
                            )
                        };
                        break;

                    case 'boolean':
                        columnDef.Cell = ({ cell }: any) => cell.getValue() ? 'Yes' : 'No';
                        break;
                }
            }

            return columnDef;
        });

        return baseColumns;
    }, [config.columns, cultureInfoName]);

    const table = useMaterialReactTable({
        columns,
        data,
        // enablePagination: false,
        // enableBottomToolbar: false,
        enablePagination: true,
        enableBottomToolbar: true,
        enableGlobalFilter: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableColumnActions: false,
        enableColumnDragging: false,
        enableStickyFooter: true,
        enablePinning: true,
        enableColumnResizing: true,
        layoutMode: 'semantic',
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 50,
            },
        },
        muiPaginationProps: {
            color: 'primary',
            shape: 'rounded',
            variant: 'outlined',
            showRowsPerPage: false,
        },
        paginationDisplayMode: 'pages',
        muiTableHeadCellProps: ({ column }) => ({
            align:
                (column.columnDef as any).type?.toLowerCase() === "currencydecimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "decimal" ||
                    (column.columnDef as any).type?.toLowerCase() === "basecurrencynetamount" ||
                    (column.columnDef as any).type?.toLowerCase() === "number"
                    ? "right"
                    : "left",
            sx: {
                border: '1px solid #e7e7e7',

            },
        }),
        muiTableBodyCellProps: ({ column }) => ({
            sx: {
                border: '1px solid #e7e7e7',
            },
        }),
        muiTableFooterCellProps: {
            sx: {
                display: config.summaryConfig.length === 0 ? 'none' : "",
                fontWeight: 'bold',
            }
        },
        muiTableFooterRowProps: {
            sx: {
                '& td': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& td:first-of-type': {
                    fontWeight: 'bold',
                    textAlign: 'left',
                    paddingLeft: '16px'
                }
            }
        },
        muiTableContainerProps: {
            sx: {
                '&::-webkit-scrollbar': {
                    width: '12px',
                    height: '12px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#999',
                    borderRadius: '8px',
                },
                scrollbarWidth: 'auto',
                scrollbarColor: '#999 transparent',
                maxHeight: 'calc(100vh - 230px)',
            }
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: 'flex',
                    gap: '16px',
                    padding: '8px',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    disabled={table.getPrePaginationRowModel().rows.length === 0 || exportLoading.pdf}
                    // onClick={handleExportPDF}
                    onClick={() => setIsOpen(true)}
                    startIcon={exportLoading.pdf ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                    sx={{ minWidth: '120px' }}
                >
                    {exportLoading.pdf ? 'Exporting...' : 'Export PDF'}
                </Button>
                <Button
                    disabled={table.getPrePaginationRowModel().rows.length === 0 || exportLoading.csv}
                    onClick={handleExportCSV}
                    startIcon={exportLoading.csv ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                    sx={{ minWidth: '120px' }}
                >
                    {exportLoading.csv ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button
                    disabled={table.getPrePaginationRowModel().rows.length === 0 || exportLoading.xls}
                    onClick={() => handleExportXLS()}
                    startIcon={exportLoading.xls ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                >
                    {exportLoading.csv ? 'Exporting...' : 'Export XLSX'}
                </Button>

            </Box>
        ),
    });

    const handleExportCSV = async () => {
        setExportLoading(prev => ({ ...prev, csv: true }));
        try {
            const originalPagination = {
                pageIndex: table.getState().pagination.pageIndex,
                pageSize: table.getState().pagination.pageSize,
            };
            const totalRows = table.getPrePaginationRowModel().rows.length;
            table.setPagination({
                pageIndex: 0,
                pageSize: totalRows,
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            const tableElement = document.querySelector('.MuiTable-root') as HTMLElement;
            if (!tableElement) return;
 
            const headers = Array.from(tableElement.querySelectorAll('thead tr:last-child th'))
                .map(th => {
                    const header = th as HTMLElement
                    let text = header.innerText.trim();
                    text = text.endsWith('0') ? text.slice(0, -1) : text;
                    return text.replace(/\n/g, ' ').replace(/\s+/g, ' ');//text.replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/,/g, '');
                });
 
            const rows = Array.from(tableElement.querySelectorAll('tbody tr'))
                .map(tr => Array.from(tr.querySelectorAll('td'))
                    .map(td => {
                        let text = td.innerText.trim();
                        return text.replace(/\n/g, ' ').replace(/\s+/g, ' ');//text.replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/,/g, '');
                    })
                );
 
            const footerRows = Array.from(tableElement.querySelectorAll('tfoot tr'))
                .map(tr => Array.from(tr.querySelectorAll('td'))
                    .map(td => {
                        let text = td.innerText.trim();
                        return text.replace(/\n/g, ' ').replace(/\s+/g, ' ');//text.replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/,/g, '');
                    })
                );
 
            const emptyColumns = headers.map((header, index) =>
                !header && rows.every(row => !row[index]) && footerRows.every(row => !row[index])
            );
 
            const filteredHeaders = headers.filter((_, index) => !emptyColumns[index]);
            const filteredRows = rows.map(row => row.filter((_, index) => !emptyColumns[index]));
            const filteredFooterRows = footerRows.map(row => row.filter((_, index) => !emptyColumns[index]));
 
            if (filteredHeaders.length === 0 || filteredRows.every(row => row.every(cell => !cell))) {
                alert("No valid data to export.");
                return;
            }
 
            const csvData = [];
 
            csvData.push([reportName]);
 
            if (showFilterInformationOnExport && selectedFilters && Object.keys(selectedFilters).length > 0) {
                Object.entries(selectedFilters).forEach(([key, value]) => {
                    csvData.push([value]);
                });
            }
 
            csvData.push([]);
 
            csvData.push(filteredHeaders);
 
            filteredRows.forEach(row => csvData.push(row));
 
            // csvData.push([]);
 
            filteredFooterRows.forEach(row => csvData.push(row));
            // const csvContent = csvData.map(row =>
            //     row.map(cell => {
            //         const escaped = cell.replace(/"/g, '""');
            //         return cell.includes(',') || cell.includes('"') ? `"${escaped}"` : escaped;
            //     }).join(',')
            // ).join('\n');
            const csvContent = csvData.map(row =>
                row.map(cell => {
                    let text = String(cell).replace(/"/g, '""');
                    if (/^\d{2}-\d{2}-\d{2}$/.test(text)) {
                        text = `'${text}`;
                    }
                    return `"${text}"`;
                }).join(',')
            ).join('\n');
            // const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const BOM = '\uFEFF';
            const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
 
            const now = new Date();
            const filename = `LiveReport`;
 
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
 
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            table.setPagination(originalPagination);
 
        } catch (error) {
            console.log(error);
        } finally {
            setExportLoading(prev => ({ ...prev, csv: false }));
        }
    };

    const handleExportPDF = async (settings: any) => {
        setExportLoading(prev => ({ ...prev, pdf: true }));
        const { orientation, format, tableWidth } = settings;
        try {
            const originalPagination = {
                pageIndex: table.getState().pagination.pageIndex,
                pageSize: table.getState().pagination.pageSize,
            };
            const totalRows = table.getPrePaginationRowModel().rows.length;
            table.setPagination({
                pageIndex: 0,
                pageSize: totalRows,
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            const tableElement = document.querySelector(".MuiTable-root") as HTMLElement;
            if (!tableElement) return;

            const tempContainer = document.createElement("div");
            tempContainer.style.position = "absolute";
            tempContainer.style.left = "-9999px";
            tempContainer.style.visibility = "hidden";
            document.body.appendChild(tempContainer);

            const tableClone = tableElement.cloneNode(true) as HTMLElement;
            tempContainer.appendChild(tableClone);

            let headers = Array.from(tableClone.querySelectorAll("thead tr th")).map((th) => {
                const header = th as HTMLElement;
                let text = header.textContent?.trim() || '';
                return text.endsWith("0") ? text.slice(0, -1) : text;
            });

            const rows = Array.from(tableClone.querySelectorAll("tbody tr")).map((row) =>
                Array.from(row.querySelectorAll("td")).map((td) => {
                    let text = td.textContent?.trim() || '';
                    text = text.replace(/\u200F|\u200E|\u202A|\u202B|\u202C|\u202D|\u202E/g, ''); 
                    text = text.replace(/(\d)\s+(?=\d)/g, '$1');
                    // return text.length > 40 ? (text.substring(0, 40) + "...") : text;
                    return text;
                })
            );

            const footerRows = Array.from(tableElement.querySelectorAll("tfoot tr")).map((row) =>
                Array.from(row.querySelectorAll("td")).map((td) => {
                    let text = td.textContent?.trim() || '';
                    text = text.replace(/\u200F|\u200E|\u202A|\u202B|\u202C|\u202D|\u202E/g, ''); 
                    text = text.replace(/(\d)\s+(?=\d)/g, '$1');
                    return text;
                })
            );

            const emptyColumns = headers.map((header, index) =>
                !header && rows.every(row => !row[index]) && footerRows.every(row => !row[index])
            );

            const filteredHeaders = headers.filter((_, index) => !emptyColumns[index]);
            const filteredRows = rows.map((row) => row.filter((_, index) => !emptyColumns[index]));
            const filteredFooterRows = footerRows.map((row) => row.filter((_, index) => !emptyColumns[index]));

            if (filteredHeaders.length === 0) {
                document.body.removeChild(tempContainer);
                alert("No valid data to export.");
                return;
            }

            const pageFormat = format;//"a4";
            const userOrientation = orientation;
            const finalFontSize = 8;
            const finalCellPadding = 2;

            const pdf = new jsPDF({
                orientation: userOrientation as any,
                unit: "mm",
                format: pageFormat as any,
                compress: true
            });

            const pageSize = pdf.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            const logoWidth = 30;
            const logoHeight = 15;
            let yOffset = 15;

            const loadImageFromURL = (url: string): Promise<string> => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = "Anonymous";
                    img.onload = () => {
                        const canvas = document.createElement("canvas");
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext("2d");
                        if (!ctx) return reject("Canvas error");
                        ctx.drawImage(img, 0, 0);
                        const dataURL = canvas.toDataURL("image/png");
                        resolve(dataURL);
                    };
                    img.onerror = (e) => reject("Image load error");
                    img.src = url;
                });
            };

            if (showCompanyLogo && companyLogo) {
                try {
                    if (isCompanyOwnLogo) {
                        const logoDataUrl = await loadImageFromURL(companyLogo);
                        pdf.addImage(logoDataUrl, 'PNG', 10, 5, logoWidth, logoHeight);
                    } else {
                        pdf.addImage(toAbsoluteUrl('/media/logos/LiveTecs_new-logo.png'), 'PNG', 10, 5, logoWidth, logoHeight);
                    }
                    yOffset = 5 + logoHeight + 5;
                } catch (error) {
                    console.warn("Logo Error", error);
                    pdf.addImage(toAbsoluteUrl('/media/logos/LiveTecs_new-logo.png'), 'PNG', 10, 5, logoWidth, logoHeight);
                    yOffset = 5 + logoHeight + 5;
                }
            }

            pdf.setFontSize(14);
            pdf.text(reportName, 10, yOffset);
            yOffset += 8;
            pdf.setFontSize(10);

            if (showFilterInformationOnExport && selectedFilters && Object.keys(selectedFilters).length > 0) {
                pdf.text("Selected Filters:", 10, yOffset);
                yOffset += 6;
                pdf.setFontSize(9);
                Object.entries(selectedFilters).forEach(([key, value]) => {
                    pdf.text(`${value}`, 10, yOffset);
                    yOffset += 5;
                });
                yOffset += 4;
            }

            const alignmentByAccessor: Record<string, 'left' | 'right'> = {};
            const columnAlignments = config.columns?.map((col: any) => {
                const accessorKey = col.accessorKey?.toLowerCase() || '';
                const isNumeric = col.type?.toLowerCase() === "number" ||
                    col.type?.toLowerCase() === "decimal" ||
                    col.type?.toLowerCase() === "currencydecimal" ||
                    accessorKey.includes('basecurrency');
                // col.accessorKey === 'BaseCurrencyNetAmount';
                // return isNumeric ? 'right' : 'left';
                alignmentByAccessor[accessorKey] = isNumeric ? 'right' : 'left';
            }) || [];

            const columnAlignmentsByIndex = filteredHeaders.map((header) => {
                const headerText = header?.toLowerCase() || '';
                const matchedColumn = config.columns?.find(
                    (col: any) =>
                        col.header?.toLowerCase() === headerText ||
                        col.accessorKey?.toLowerCase() === headerText.replace(/\s+/g, '')
                );
                const accessorKey = matchedColumn?.accessorKey?.toLowerCase() || '';
                return alignmentByAccessor[accessorKey] || 'left';
            });

            const totalPagesExp = "{total_pages_count_string}";
            autoTable(pdf, {
                head: [filteredHeaders],
                body: filteredRows,
                foot: filteredFooterRows.every(row => row.every(cell => cell === ""))
                    ? undefined
                    : filteredFooterRows,
                startY: yOffset,
                margin: { left: 5, right: 5, top: 10, bottom: 10 },
                theme: 'grid',
                tableWidth: tableWidth ?? 'auto', //'auto',

                styles: {
                    fontSize: finalFontSize,
                    cellPadding: finalCellPadding,
                    overflow: "linebreak",
                    valign: "top",
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1,
                    font: "helvetica"
                },

                headStyles: {
                    fillColor: "black",
                    textColor: 255,
                    fontStyle: "bold",
                    fontSize: finalFontSize + 1,
                    halign: 'center',
                    valign: 'middle'
                },

                bodyStyles: {
                    textColor: [0, 0, 0],
                },

                footStyles: {
                    fillColor: [240, 240, 240],
                    textColor: [0, 0, 0],
                    fontStyle: "bold",
                    fontSize: finalFontSize
                },

                showFoot: "lastPage",
                columnStyles: filteredHeaders.reduce((acc: any, header, index) => {
                    const headerText = header?.toLowerCase() || '';
                    const matchedColumn = config.columns?.find(
                        (col: any) =>
                            col.header?.toLowerCase() === headerText ||
                            col.accessorKey?.toLowerCase() === headerText.replace(/\s+/g, '')
                    );
                    const accessorKey = matchedColumn?.accessorKey?.toLowerCase() || '';
                    const isDescription = headerText.includes('description');

                    acc[index] = {
                        halign: alignmentByAccessor[accessorKey] || 'left',
                        cellWidth: isDescription ? 50 : 'auto',
                    };

                    return acc;
                }, {}),
                // columnStyles: filteredHeaders.reduce((acc: any, _, index) => {
                //     acc[index] = {
                //         halign: columnAlignments[index] || 'left',
                //     };
                //     return acc;
                // }, {}),
                didParseCell: function (data: any) {
                    if (data.section === 'foot') {
                        const align = columnAlignmentsByIndex[data.column.index];
                        if (align) data.cell.styles.halign = align;
                    }
                },
                didDrawPage: function (data: any) {
                    pdf.setFontSize(8);
                    pdf.setTextColor(100);
                    pdf.text(
                        `Page ${data.pageNumber} of ${totalPagesExp}`,
                        data.settings.margin.left,
                        pageHeight - 5
                    );
                },
            });

            document.body.removeChild(tempContainer);
            if (typeof pdf.putTotalPages === 'function') {
                pdf.putTotalPages(totalPagesExp);
            }
            table.setPagination(originalPagination);
            pdf.save("LiveReport.pdf");

        } catch (error) {
            console.log(error);
        } finally {
            setExportLoading(prev => ({ ...prev, pdf: false }));
        }
    };

    const handleExportXLS = async () => {
        setExportLoading(prev => ({ ...prev, xls: true }));
        try {
            const originalPagination = {
                pageIndex: table.getState().pagination.pageIndex,
                pageSize: table.getState().pagination.pageSize,
            };
            const totalRows = table.getPrePaginationRowModel().rows.length;
            table.setPagination({
                pageIndex: 0,
                pageSize: totalRows,
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            const tableElement = document.querySelector('.MuiTable-root');
            if (!tableElement) return;
            const wb = XLSX.utils.book_new();
            const wsData: any[][] = [];
 
            wsData.push([reportName]);
            if (showFilterInformationOnExport && selectedFilters && Object.keys(selectedFilters).length > 0) {
                Object.entries(selectedFilters).forEach(([key, value]) => {
                    wsData.push([value]);
                });
            }
            wsData.push([]);
 
            const headerRows = Array.from(tableElement.querySelectorAll('thead tr'));
            const headers = headerRows[headerRows.length - 1]
                ? Array.from(headerRows[headerRows.length - 1].querySelectorAll('th'))
                    .map(th => {
                        let text = th.textContent?.trim() || '';
                        return text.endsWith('0') ? text.slice(0, -1) : text;
                    })
                : [];
 
            const rows = Array.from(tableElement.querySelectorAll('tbody tr')).map(tr =>
                Array.from(tr.querySelectorAll('td')).map(td => td.textContent?.trim() || '')
            );
 
            const footerRows = Array.from(tableElement.querySelectorAll('tfoot tr')).map(tr =>
                Array.from(tr.querySelectorAll('td')).map(td => td.textContent?.trim() || '')
            );
 
            const emptyColumns = headers.map((header, index) =>
                !header && rows.every(row => !row[index]) && footerRows.every(row => !row[index])
            );
 
            const filteredHeaders = headers.filter((_, index) => !emptyColumns[index]);
            const filteredRows = rows.map(row => row.filter((_, index) => !emptyColumns[index]));
            const filteredFooterRows = footerRows.map(row => row.filter((_, index) => !emptyColumns[index]));
 
            wsData.push(filteredHeaders);
            filteredRows.forEach(row => wsData.push(row));
            filteredFooterRows.forEach(row => wsData.push(row));
 
            const ws = XLSX.utils.aoa_to_sheet(wsData);
            ws['!cols'] = filteredHeaders.map(() => ({ wch: 20 }));
 
            if (ws['!ref']) {
                const range = XLSX.utils.decode_range(ws['!ref']);
 
                const columnAlignments = filteredHeaders.map((header, colIndex) => {
 
                    const columnDef = config.columns?.find((col: any) =>
                        col.header === header ||
                        col.accessorKey === header ||
                        col.id === header
                    );
 
                    if (columnDef) {
                        const type = columnDef.type?.toLowerCase();
                        const accessor = columnDef.accessorKey?.toLowerCase();
 
                        if (type === 'currencydecimal' ||
                            type === 'decimal' ||
                            type === 'number' ||
                            (accessor && [
                                'basecurrencyamount',
                                'basecurrencyestimatedcost',
                                'basecurrencyemployeecost'
                            ].includes(accessor))) {
                            return 'right';
                        }
                    }
                    return 'left';
                });
 
                filteredRows.forEach((rowData, index) => {
                    const excelRowIndex = index + wsData.length - filteredRows.length - filteredFooterRows.length;
 
                    for (let c = 0; c < rowData.length; c++) {
                        const cellAddress = XLSX.utils.encode_cell({ r: excelRowIndex, c });
                        if (!ws[cellAddress]) ws[cellAddress] = { v: rowData[c] };
                        const hasComma = rowData[c] && rowData[c].includes(',');
                        const valueWithoutComma = hasComma ? rowData[c].replace(',', '.') : rowData[c];
                        const isNumeric = columnAlignments[c] === 'right' && !isNaN(parseFloat(valueWithoutComma));
                        if (isNumeric && hasComma) {
                            ws[cellAddress].v = rowData[c];
                            ws[cellAddress].t = 's'
                        } else if (columnAlignments[c] === 'right' && !isNaN(parseFloat(rowData[c]))) {
                            ws[cellAddress].v = parseFloat(rowData[c].replace(',', '.'));
                            ws[cellAddress].t = 'n';
                            ws[cellAddress].z = '0.00';
                        }
                        ws[cellAddress].s = {
                            alignment: {
                                horizontal: columnAlignments[c],
                                vertical: "center"
                            },
                            border: {
                                top: { style: 'thin', color: { rgb: "A6A6A6" } },
                                left: { style: 'thin', color: { rgb: "A6A6A6" } },
                                bottom: { style: 'thin', color: { rgb: "A6A6A6" } },
                                right: { style: 'thin', color: { rgb: "A6A6A6" } },
                            }
                        };
                    }
                });
 
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const headerCell = XLSX.utils.encode_cell({
                        r: wsData.length - filteredRows.length - filteredFooterRows.length - 1,
                        c: C
                    });
                    if (!ws[headerCell]) continue;
 
                    ws[headerCell].s = {
                        fill: { fgColor: { rgb: "000000" } },
                        font: { color: { rgb: "FFFFFF" }, bold: true },
                        alignment: { horizontal: "center", vertical: "center" },
                        border: {
                            top: { style: 'thin', color: { rgb: "A6A6A6" } },
                            left: { style: 'thin', color: { rgb: "A6A6A6" } },
                            bottom: { style: 'thin', color: { rgb: "A6A6A6" } },
                            right: { style: 'thin', color: { rgb: "A6A6A6" } },
                        }
                    };
                }
 
                for (let i = 0; i < filteredFooterRows.length; i++) {
                    for (let C = range.s.c; C <= range.e.c; ++C) {
                        const footerCell = XLSX.utils.encode_cell({
                            r: wsData.length - filteredFooterRows.length + i,
                            c: C
                        });
                        if (!ws[footerCell]) continue;
   const hasComma = filteredFooterRows[i][C] && filteredFooterRows[i][C].includes(',');
                        const valueWithoutComma = hasComma ? filteredFooterRows[i][C].replace(',', '.') : filteredFooterRows[i][C];
                        const isNumeric = columnAlignments[C] === 'right' && !isNaN(parseFloat(valueWithoutComma));
                        if (isNumeric && hasComma) {
                            ws[footerCell].v = filteredFooterRows[i][C];
                            ws[footerCell].t = 's'
                        } else if (columnAlignments[C] === 'right' && !isNaN(parseFloat(filteredFooterRows[i][C]))) {
                            ws[footerCell].v = parseFloat(filteredFooterRows[i][C].replace(',', '.'));
                            ws[footerCell].t = 'n';
                            ws[footerCell].z = '0.00';
                        } 
                        ws[footerCell].s = {
                            // fill: { fgColor: { rgb: "F5F5F5" } },
                            // font: { bold: true },
                            fill: { fgColor: { rgb: "000000" } },
                            font: { color: { rgb: "FFFFFF" }, bold: true },
                            alignment: {
                                horizontal: columnAlignments[C],
                                vertical: "center"
                            },
                            border: {
                                top: { style: 'thin', color: { rgb: "A6A6A6" } },
                                left: { style: 'thin', color: { rgb: "A6A6A6" } },
                                bottom: { style: 'thin', color: { rgb: "A6A6A6" } },
                                right: { style: 'thin', color: { rgb: "A6A6A6" } },
                            }
                        };
                    }
                }
            }
 
            XLSX.utils.book_append_sheet(wb, ws, "Report");
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `LiveReport_${timestamp}.xlsx`;
            // XLSX.writeFile(wb, fileName);
            const wbout = XLSX.write(wb, {
                bookType: "xlsx",
                type: "array",
                compression: true
            });
            const blob = new Blob([wbout], { type: "application/octet-stream" });
            saveAs(blob, fileName);
            table.setPagination(originalPagination);
 
        } catch (error) {
            console.log(error);
        } finally {
            setExportLoading(prev => ({ ...prev, xls: false }));
        }
    };

    const handlePDFSubmit = (settings: any) => {
        handleExportPDF(settings);
        setIsOpen(false);
    };

    const onClose = () => {
        setIsOpen(false);
    }
    return (
        <>
            <MaterialReactTable table={table} />
            {isOpen && <ExportToPDFModal isOpen={isOpen} onClose={onClose} onSubmit={handlePDFSubmit} />}
        </>
    )
};

export default MUISimpleTableView;